import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { PublicationProductModel, PublicationProductRequestOptions } from '@models/types'

const watchEntityKeys = ['publicationProducts']

type PublicationProductFilters = {
  publication_id?: number,
}

type UsePublicationProductsOptions = {
  entityKey?: string,
  filters?: PublicationProductFilters,
  performHttpRequests?: boolean,
  requestOptions?: PublicationProductRequestOptions,
}

function usePublicationProducts(options: UsePublicationProductsOptions = {}) {
  const { entityKey, filters } = options
  const {
    publication_id: filterPublicationId,
  } = filters || {}

  const {
    updatedEntities: { publicationProducts: publicationProductsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const publicationProducts: Record<string, PublicationProductModel> = useSelector(
    reduxState => reduxState.entities.publicationProducts,
  )

  const filteredPublicationProducts = useMemo(() => {
    const filtered = Object.values(publicationProducts)
      .filter((publicationProduct) => {
        const { publication_id } = publicationProduct

        const matchPublicationId = matchFilterNumber(publication_id, filterPublicationId)

        return matchPublicationId
      })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [publicationProductsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationProductsCount = filteredPublicationProducts.length
  const hasFilteredPublicationProducts = !!filteredPublicationProductsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationProductsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPublicationProducts } = useReduxAction(
    'publicationProducts',
    'loadPublicationProducts',
    {
      ...filtersWithOffset,
      entityKey,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationProducts,
    filteredPublicationProductsCount,
    hasFilteredPublicationProducts,
    loading: loadingPublicationProducts,
  }
}

export default usePublicationProducts
